import { ENCOUNTER_MODULES, MONEY_AUTHORITY_MODULES, PATIENT_BEARING_MODULES, modulesOfScreen, type ModuleCode } from '@his/shared';
import {
  Archive,
  Activity, BedDouble, Building2, CalendarDays, ClipboardList, CreditCard, FlaskConical,
  LayoutDashboard, Package, Pill, Receipt, ScanLine, Settings, ShieldCheck, Stethoscope,
  Users, UserSquare2, Scissors, HeartPulse, Landmark, BriefcaseMedical, FileText, Bell,
  Wallet, FolderArchive, CalendarClock, Droplet, Truck, UtensilsCrossed, Sparkles, MessageSquare, ListTodo,
  Baby, Waves, Smile, Wrench, LineChart, LifeBuoy, RefreshCw, Home, Globe, ShoppingBag, KeyRound, ClipboardCheck,
  RotateCcw, HandCoins, AlertTriangle, BarChart3, MapPin, Search,
  CalendarCheck, Hourglass, Percent, Scale, Clock,
  Banknote, Calculator, Hospital, Siren, ListChecks,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  group?: string;
  /** Hidden unless the tenant has licensed this module (SRS 5.5, enforcement layer 1). */
  module?: ModuleCode;
  /**
   * Hidden unless the tenant has at least one of these. Patient records serve
   * a dozen modules, so tying them to a single one would hide them from a lab
   * or show them to a medical store that only ever sells over the counter.
   */
  anyModule?: ModuleCode[];
  /** Hidden unless the signed-in user holds one of these permissions. */
  permissions?: string[];
  readOnly?: boolean;
}

export interface NavGroup {
  name: string;
  icon: LucideIcon;
}

export interface NavSection {
  title: string;
  items: NavItem[];
  groups?: NavGroup[];
}

/**
 * Staff navigation.
 *
 * An item disappears when the tenant has not bought the module *or* when the
 * user lacks the permission. Hiding is a usability measure only — the API
 * enforces both independently, so a hand-typed URL gets 403, not data.
 */
const NAVIGATION_SOURCE: NavSection[] = [
  {
    title: 'Today',
    items: [
      { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, permissions: ['dashboard.read'] },
      { label: 'My queue', to: '/queue', icon: ClipboardList, module: 'opd', permissions: ['encounter.read.own', 'encounter.write', 'vitals.write', 'appointment.manage'] },
      { label: 'Triage', to: '/triage', icon: HeartPulse, module: 'opd', permissions: ['vitals.write'] },
      { label: 'Co-sign', to: '/cosign', icon: ClipboardCheck, module: 'opd', permissions: ['encounter.cosign'] },
      { label: 'Appointments', to: '/appointments', icon: CalendarDays, module: 'opd', permissions: ['appointment.read', 'appointment.manage'] },
      { label: 'Clinic hours', to: '/schedules', icon: CalendarClock, module: 'opd', permissions: ['schedule.read', 'schedule.manage', 'schedule.manage.own'] },
      { label: 'Follow-ups due', to: '/follow-ups', icon: CalendarCheck, module: 'opd', permissions: ['appointment.read', 'appointment.manage'] },
      { label: 'Online requests', to: '/online-requests', icon: Globe, anyModule: ['opd', 'lab'], permissions: ['appointment.read', 'appointment.manage'] },
      { label: 'Reports', to: '/reports', icon: Activity, module: 'reports', permissions: ['report.read', 'report.read.all', 'report.financial', 'pharmacy.report'] },
    ],
  },
  {
    title: 'Patients',
    items: [
      { label: 'Patients', to: '/patients', icon: Users, anyModule: PATIENT_BEARING_MODULES, permissions: ['patient.read'] },
      { label: 'Register', to: '/patients/new', icon: UserSquare2, anyModule: PATIENT_BEARING_MODULES, permissions: ['patient.register'] },
      { label: 'Medical records', to: '/mrd', icon: FolderArchive, module: 'mrd', permissions: ['mrd.read', 'mrd.code'] },
    ],
  },
  {
    title: 'Clinical',
    groups: [
      { name: 'Inpatient', icon: BedDouble },
      { name: 'Emergency & theatre', icon: Siren },
      { name: 'Specialty units', icon: Hospital },
    ],
    items: [
      { label: 'Consultations', to: '/consultations', icon: Stethoscope, module: 'opd', permissions: ['encounter.write', 'encounter.read'] },
      { label: 'Bed board', to: '/ipd/beds', icon: BedDouble, group: 'Inpatient', module: 'ipd', permissions: ['bed.read'] },
      { label: 'Admissions', to: '/ipd/admissions', icon: BriefcaseMedical, group: 'Inpatient', module: 'ipd', permissions: ['admission.read'] },
      { label: 'Ward tasks', to: '/ipd/wards', icon: ListChecks, group: 'Inpatient', module: 'ipd', permissions: ['emar.read', 'emar.administer', 'nursing.note.write'] },
      { label: 'Find a patient', to: '/ipd/locator', icon: Search, group: 'Inpatient', module: 'ipd', permissions: ['visitor.locate'] },
      { label: 'Ward charges', to: '/ipd/charge-rules', icon: ClipboardList, group: 'Inpatient', module: 'ipd', permissions: ['ward.charge.manage'] },
      { label: 'Emergency', to: '/er', icon: HeartPulse, group: 'Emergency & theatre', module: 'er', permissions: ['er.read', 'er.manage'] },
      { label: 'Operation theatre', to: '/ot', icon: Scissors, group: 'Emergency & theatre', module: 'ot', permissions: ['ot.read'] },
      { label: 'Blood bank', to: '/blood-bank', icon: Droplet, group: 'Specialty units', module: 'blood_bank', permissions: ['blood_bank.manage'] },
      { label: 'Labour & delivery', to: '/maternity', icon: Baby, group: 'Specialty units', module: 'maternity', permissions: ['maternity.manage'] },
      { label: 'Therapy units', to: '/therapy', icon: Waves, group: 'Specialty units', anyModule: ['dialysis', 'daycare'], permissions: ['therapy.manage'] },
      { label: 'Dental', to: '/dental', icon: Smile, group: 'Specialty units', module: 'dental', permissions: ['dental.manage'] },
    ],
  },
  {
    title: 'Diagnostics',
    items: [
      { label: 'Laboratory', to: '/lab', icon: FlaskConical, module: 'lab', permissions: ['lab.order.read', 'lab.result.enter', 'lab.result.verify'] },
      { label: 'Critical results', to: '/lab/critical', icon: AlertTriangle, module: 'lab', permissions: ['lab.critical.acknowledge', 'lab.result.verify', 'lab.master.manage'] },
      { label: 'Home collection', to: '/lab/home-collection', icon: MapPin, module: 'lab', permissions: ['lab.collect', 'lab.homecollection.assign'] },
      { label: 'Quality control', to: '/lab/qc', icon: LineChart, module: 'lab', permissions: ['lab.result.enter', 'lab.master.manage'] },
      { label: 'Test catalogue', to: '/lab/catalogue', icon: ClipboardList, module: 'lab', permissions: ['lab.master.manage'] },
      { label: 'Lab reports', to: '/lab/reports', icon: BarChart3, module: 'lab', permissions: ['report.read', 'lab.result.verify', 'lab.master.manage'] },
      { label: 'Radiology', to: '/radiology', icon: ScanLine, module: 'radiology', permissions: ['radiology.worklist'] },
    ],
  },
  {
    title: 'Pharmacy & stock',
    items: [
      { label: 'Pharmacy POS', to: '/pharmacy', icon: Pill, module: 'pharmacy', permissions: ['pharmacy.pos', 'pharmacy.read'] },
      { label: 'Inventory', to: '/inventory', icon: Package, module: 'inventory', permissions: ['stock.read', 'product.read'] },
      { label: 'Stock control', to: '/stock-control', icon: ClipboardCheck, anyModule: ['pharmacy', 'inventory'], permissions: ['stock.take.manage', 'stock.adjust', 'stock.adjust.approve', 'stock.batch.manage', 'stock.reprice'] },
      { label: 'Online orders', to: '/online-orders', icon: ShoppingBag, module: 'pharmacy', permissions: ['pharmacy.read', 'pharmacy.pos'] },
    ],
  },
  {
    title: 'Money',
    groups: [{ name: 'Billing & cashier', icon: Banknote }],
    items: [
      { label: 'Billing', to: '/billing', icon: Receipt, group: 'Billing & cashier', module: 'billing', permissions: ['invoice.read', 'invoice.create'] },
      { label: 'OPD dues', to: '/billing/opd-dues', icon: CreditCard, group: 'Billing & cashier', module: 'billing', anyModule: ['opd'], permissions: ['payment.collect'] },
      { label: 'Cashier shift', to: '/billing/shift', icon: Clock, group: 'Billing & cashier', module: 'billing', permissions: ['shift.manage'] },
      { label: 'Refunds', to: '/billing/refunds', icon: RotateCcw, group: 'Billing & cashier', module: 'billing', permissions: ['payment.refund', 'payment.refund.approve'] },
      { label: 'Credit customers', to: '/billing/credit-customers', icon: HandCoins, group: 'Billing & cashier', module: 'billing', permissions: ['payment.collect', 'invoice.read'] },
      { label: 'Dues register', to: '/ipd/dues', icon: Hourglass, group: 'Billing & cashier', module: 'ipd', permissions: ['dues.read'] },
      { label: 'Services & prices', to: '/billing/charges', icon: FileText, group: 'Billing & cashier', module: 'billing', permissions: ['charge.read'] },
      { label: 'Billing settings', to: '/billing/settings', icon: Settings, group: 'Billing & cashier', module: 'billing', permissions: ['tenant.settings.read', 'gl.read'] },
      { label: 'Insurance', to: '/insurance', icon: ShieldCheck, module: 'insurance', permissions: ['claim.read', 'claim.manage'] },
    ],
  },
  {
    title: 'Accounts',
    groups: [{ name: 'Books', icon: Calculator }],
    items: [
      { label: 'Accounting', to: '/accounting', icon: Landmark, group: 'Books', module: 'accounting', permissions: ['gl.read'] },
      { label: 'Supplier payables', to: '/payables', icon: HandCoins, group: 'Books', anyModule: ['pharmacy', 'inventory'], permissions: ['supplier.payment.create', 'supplier.payment.approve', 'purchase.manage', 'purchase.return', 'gl.read'] },
      { label: 'Financial authority', to: '/accounts/authority', icon: Scale, anyModule: [...MONEY_AUTHORITY_MODULES], permissions: ['authority.read', 'authority.manage'] },
      { label: 'Doctor share', to: '/accounts/doctor-share', icon: Percent, module: 'billing', anyModule: ENCOUNTER_MODULES, permissions: ['doctor_share.read', 'doctor_share.manage'] },
      { label: 'Doctor payouts', to: '/accounting/payouts', icon: Wallet, module: 'accounting', anyModule: ENCOUNTER_MODULES, permissions: ['payout.manage'] },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Ambulance', to: '/ambulance', icon: Truck, module: 'ambulance', permissions: ['ambulance.manage'] },
      { label: 'Kitchen', to: '/dietary', icon: UtensilsCrossed, module: 'dietary', permissions: ['dietary.manage'] },
      { label: 'Housekeeping', to: '/housekeeping', icon: Sparkles, module: 'housekeeping', permissions: ['housekeeping.manage'] },
      { label: 'Tasks', to: '/tasks', icon: ListTodo, module: 'tasks', permissions: ['task.read', 'task.manage'] },
      { label: 'Notifications', to: '/notifications', icon: Bell, module: 'notifications', permissions: ['notification.read', 'notification.template.manage'] },
      { label: 'Feedback', to: '/feedback', icon: MessageSquare, module: 'feedback', permissions: ['feedback.read', 'feedback.manage'] },
      { label: 'Assets & maintenance', to: '/assets', icon: Wrench, module: 'inventory', permissions: ['product.read', 'store.manage'] },
    ],
  },
  {
    title: 'People',
    items: [
      { label: 'People', to: '/people', icon: Users, permissions: ['user.read', 'role.read', 'hr.employee.read', 'hr.roster.manage', 'hr.payroll.run'] },
    ],
  },
  {
    title: 'Administration',
    items: [
      { label: 'Branches & departments', to: '/admin/branches', icon: Building2, permissions: ['branch.read'] },
      { label: 'Settings', to: '/admin/settings', icon: Settings, permissions: ['tenant.settings.read'] },
      { label: 'Public listing', to: '/admin/listing', icon: Globe, permissions: ['tenant.settings.read', 'tenant.settings.manage'] },
      { label: 'Audit log', to: '/admin/audit', icon: ShieldCheck, permissions: ['audit.read'] },
    ],
  },
  {
    title: 'Help',
    items: [
      // No module gate and no read-only gate: a hospital whose subscription has
      // lapsed is exactly the one that needs to reach support.
      { label: 'Support', to: '/support', icon: LifeBuoy, permissions: ['support.ticket.read', 'support.ticket.raise'] },
    ],
  },
  {
    // Everyone signed in, whatever their role. The screen was always there and
    // was only reachable from an unlabelled key icon beside the profile, so
    // hospitals reported that staff simply could not change their password.
    title: 'Your account',
    items: [{ label: 'Security', to: '/account/security', icon: ShieldCheck }],
  },
];

export function withManifestModules(item: NavItem): NavItem {
  if (item.module && item.anyModule) return item;
  const owners = modulesOfScreen(item.to).filter((module) => module !== 'core');
  if (owners.length === 0) return item;
  return { ...item, module: undefined, anyModule: owners };
}

export const NAVIGATION: NavSection[] = NAVIGATION_SOURCE.map((section) => ({
  ...section,
  items: section.items.map(withManifestModules),
}));

export const ARCHIVED_SECTION_TITLE = 'Archived modules';

export type NavEntry =
  | { kind: 'item'; item: NavItem }
  | { kind: 'group'; name: string; icon: LucideIcon; items: NavItem[] };

export function sectionEntries(section: NavSection): NavEntry[] {
  const entries: NavEntry[] = [];
  const groups = new Map<string, Extract<NavEntry, { kind: 'group' }>>();
  for (const item of section.items) {
    const group = item.group ? section.groups?.find((candidate) => candidate.name === item.group) : undefined;
    if (!group) {
      entries.push({ kind: 'item', item });
      continue;
    }
    const existing = groups.get(group.name);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    const created: Extract<NavEntry, { kind: 'group' }> = { kind: 'group', name: group.name, icon: group.icon, items: [item] };
    groups.set(group.name, created);
    entries.push(created);
  }
  return entries.map((entry) =>
    entry.kind === 'group' && entry.items.length === 1 ? { kind: 'item', item: entry.items[0] } : entry,
  );
}

export const sectionKey = (section: NavSection) => `section:${section.title}`;
export const groupKey = (section: NavSection, group: string) => `group:${section.title}:${group}`;

export function itemMatchesPath(item: { to: string }, pathname: string): boolean {
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function longestPrefixMatch<T extends { to: string }>(items: Iterable<T>, pathname: string): T | undefined {
  let best: T | undefined;
  for (const item of items) {
    if (!itemMatchesPath(item, pathname)) continue;
    if (!best || item.to.length > best.to.length) best = item;
  }
  return best;
}

export const NAV_ITEMS: readonly NavItem[] = NAVIGATION.flatMap((section) => section.items);

function bestNavMatch(sections: NavSection[], pathname: string): { item: NavItem; keys: string[] } | undefined {
  let best: { item: NavItem; keys: string[] } | undefined;
  for (const section of sections) {
    for (const entry of sectionEntries(section)) {
      const items = entry.kind === 'item' ? [entry.item] : entry.items;
      for (const item of items) {
        if (!itemMatchesPath(item, pathname)) continue;
        if (best && item.to.length <= best.item.to.length) continue;
        const keys = entry.kind === 'group' ? [sectionKey(section), groupKey(section, entry.name)] : [sectionKey(section)];
        best = { item, keys };
      }
    }
  }
  return best;
}

export function activeNavKeys(sections: NavSection[], pathname: string): string[] {
  return bestNavMatch(sections, pathname)?.keys ?? [];
}

export function activeNavPath(sections: NavSection[], pathname: string): string | undefined {
  return bestNavMatch(sections, pathname)?.item.to;
}

export function matchingNavItems(sections: NavSection[], query: string): { section: string; item: NavItem }[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return sections.flatMap((section) =>
    section.items
      .filter((item) => [item.label, item.group ?? '', section.title].some((text) => text.toLowerCase().includes(needle)))
      .map((item) => ({ section: section.title, item })),
  );
}

/**
 * The module a path belongs to, by longest matching nav entry.
 *
 * The navigation already declares which module every screen needs; reusing it
 * means a route can never drift away from the link that reaches it.
 */
export function modulesForPath(pathname: string): ModuleCode[] | undefined {
  let best: { length: number; modules?: ModuleCode[] } | null = null;
  for (const section of NAVIGATION) {
    for (const item of section.items) {
      const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (!matches) continue;
      const modules = item.anyModule ?? (item.module ? [item.module] : undefined);
      if (!best || item.to.length > best.length) best = { length: item.to.length, modules };
    }
  }
  return best?.modules;
}

export function permissionsForPath(pathname: string): string[] | undefined {
  let best: { length: number; permissions?: string[] } | null = null;
  for (const section of NAVIGATION) {
    for (const item of section.items) {
      const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (!matches) continue;
      if (!best || item.to.length > best.length) best = { length: item.to.length, permissions: item.permissions };
    }
  }
  return best?.permissions;
}

export function permitsPath(pathname: string, can: (...permissions: string[]) => boolean): boolean {
  const required = permissionsForPath(pathname);
  return !required || required.length === 0 || can(...required);
}

/** True when the tenant holds at least one of the modules a screen needs. */
export function allowsPath(
  pathname: string,
  hasModule: (module: ModuleCode) => boolean,
  isRetired: (module: ModuleCode) => boolean = () => false,
): boolean {
  const required = modulesForPath(pathname);
  return !required || required.length === 0 || required.some(hasModule) || required.some(isRetired);
}

export function visibleSections(
  can: (...permissions: string[]) => boolean,
  hasModule: (module: ModuleCode) => boolean,
  hide: (item: NavItem) => boolean = () => false,
  isRetired: (module: ModuleCode) => boolean = () => false,
): NavSection[] {
  const archived: NavItem[] = [];
  const licensed = (item: NavItem) =>
    (!item.module || hasModule(item.module)) && (!item.anyModule || item.anyModule.some(hasModule));
  const retired = (item: NavItem) =>
    [...(item.module ? [item.module] : []), ...(item.anyModule ?? [])].some(isRetired) &&
    (!item.module || hasModule(item.module) || isRetired(item.module));
  const live = NAVIGATION.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (hide(item)) return false;
      if (item.permissions && !can(...item.permissions)) return false;
      if (licensed(item)) return true;
      if (retired(item) && !archived.some((entry) => entry.to === item.to)) archived.push({ ...item, group: undefined, readOnly: true });
      return false;
    }),
  })).filter((section) => section.items.length > 0);
  if (archived.length === 0) return live;
  const help = live.findIndex((section) => section.title === 'Help');
  const archive: NavSection = { title: ARCHIVED_SECTION_TITLE, items: archived };
  return help < 0 ? [...live, archive] : [...live.slice(0, help), archive, ...live.slice(help)];
}

/** Owner console navigation — platform staff only, never tenant users. */
export const PLATFORM_NAVIGATION: NavItem[] = [
  { label: 'Overview', to: '/owner', icon: LayoutDashboard },
  { label: 'Tenants', to: '/owner/tenants', icon: Building2 },
  { label: 'Tickets', to: '/owner/tickets', icon: LifeBuoy },
  { label: 'Renewals', to: '/owner/renewals', icon: RefreshCw },
  { label: 'Plans', to: '/owner/plans', icon: CreditCard },
  { label: 'Patient changes', to: '/owner/person-changes', icon: UserSquare2 },
  { label: 'Accounts', to: '/owner/staff', icon: Users },
  { label: 'Deletion backups', to: '/owner/deletion-backups', icon: Archive },
  { label: 'Database backups', to: '/owner/backups', icon: Archive },
];

/**
 * The client portal's navigation — a contact person's whole world.
 *
 * Three items on purpose. Their remit is the hospitals named to them, those
 * hospitals' tickets, and their renewals; anything else on this list would be
 * something they cannot actually reach.
 */
export const CLIENT_NAVIGATION: NavItem[] = [
  { label: 'My hospitals', to: '/client', icon: Home },
  { label: 'Tickets', to: '/client/tickets', icon: LifeBuoy },
  { label: 'Renewals', to: '/client/renewals', icon: RefreshCw },
];
